import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { League } from '@sorare/core/src/components/landing/types';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from '#src/components/landing/WalletLanding/index.tsx';
import { winAssets } from '#src/pages/assets/assets.ts';

import bg from './assets/hero/bg.webp';
import leftSidePlayers from './assets/hero/players/leftSidePlayers.webp';
import rightSidePlayers from './assets/hero/players/rightSidePlayers.webp';
import leagueLogo from './assets/league-logo.webp';
import field from './assets/play/field.webp';
import lineup from './assets/play/lineup.webp';
import scoutBg from './assets/scout/background.webp';
import players from './assets/scout/players.webp';
import social from './assets/social.webp';

const messages = defineMessages({
  metadatasTitle: {
    defaultMessage: 'The Real Fantasy Ligue 1 Game – Sorare Football',
  },
  metadatasDescription: {
    defaultMessage:
      "Collect, play and win officially licensed digital cards featuring players from the Ligue 1, the world's best football league on Sorare",
  },
});

const heroAssets = {
  background: bg,
  players: [
    {
      pictureUrl: leftSidePlayers,
      height: 430,
      width: 658,
    },
    {
      pictureUrl: rightSidePlayers,
      height: 432,
      width: 557,
    },
  ],
};

const scoutAssets = {
  background: scoutBg,
  players,
};

const playAssets = {
  background: field,
  lineup,
};

const FootballPage = () => {
  const { generateSportContextPath } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: social,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  if (isLoggedIn) {
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} replace />;
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_FRANCE_LIMITED,
        So5LeaderboardType.IN_SEASON_FRANCE_RARE,
        So5LeaderboardType.IN_SEASON_FRANCE_SUPER_RARE,
      ]}
      leagueLogo={
        <Img src={leagueLogo} alt="Ligue 1" width="240" height="80" />
      }
      league={League.Ligue1}
    />
  );
};

export default FootballPage;
